/**
 * usePolling — 轮询 Hook
 *
 * 功能：
 *  - 首次对话完成后调用 startPolling() 启动轮询
 *  - 每次轮询后使用响应中的 duration（毫秒）决定下次间隔
 *  - 当有未读消息时追加到 store
 *  - 组件卸载时自动停止轮询
 */
import { useCallback, useEffect, useRef } from 'react';
import wsuiApi from '@api/wsuiApi';
import type { IMessage } from '@typing/wsui/getMessages';
import { convertMsgDataToMessages } from '@pages/chat/hooks/useHistoryMessages';

interface UsePollingOptions {
    sessionId: string;
    /** 轮询到新消息后的回调 */
    onNewMessages?: (messages: IMessage[]) => void;
}

const DEFAULT_INTERVAL_MS = 5000;

export const usePolling = ({ sessionId, onNewMessages }: UsePollingOptions) => {
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const maxSendTimeRef = useRef<number>(0);
    const activeRef = useRef(false);
    const sessionIdRef = useRef(sessionId);

    // Keep sessionIdRef in sync without restarting polling
    useEffect(() => {
        sessionIdRef.current = sessionId;
    }, [sessionId]);

    const stopPolling = useCallback(() => {
        activeRef.current = false;
        if (timerRef.current !== null) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const poll = useCallback(async () => {
        if (!activeRef.current || !sessionIdRef.current) return;

        let nextInterval = DEFAULT_INTERVAL_MS;
        try {
            const response = await wsuiApi.polling({
                unReadMsgParam: {
                    maxSendTime: maxSendTimeRef.current,
                    sessionId: sessionIdRef.current,
                },
                businessType: 'doctor_youyi',
                appKey: 'pc_wenzhen',
            });

            if (response.errno === 0 && response.data) {
                const { duration, unreadList } = response.data;
                if (typeof duration === 'number' && duration > 0) {
                    nextInterval = duration;
                }

                if (unreadList?.msgData && unreadList.msgIds?.length > 0) {
                    const converted = convertMsgDataToMessages(unreadList.msgData, unreadList.msgIds);
                    if (converted.length > 0) {
                        onNewMessages?.(converted);
                    }
                }
                if (unreadList?.maxSendTime) {
                    maxSendTimeRef.current = unreadList.maxSendTime;
                }
            }
        } catch {
            // silent — keep polling on errors
        }

        if (activeRef.current) {
            timerRef.current = setTimeout(poll, nextInterval);
        }
    }, [onNewMessages]);

    const startPolling = useCallback(() => {
        if (activeRef.current) return; // already running
        if (!sessionIdRef.current) return;
        activeRef.current = true;
        maxSendTimeRef.current = 0;
        // first poll fires after 5s delay
        timerRef.current = setTimeout(poll, DEFAULT_INTERVAL_MS);
    }, [poll]);

    // Stop on unmount
    useEffect(() => {
        return () => {
            stopPolling();
        };
    }, [stopPolling]);

    return { startPolling, stopPolling };
};
